import "../styles/global.css";
import "../styles/global-sass.scss";
import "../styles/variables.scss";
import "../styles/chart-tw.css";
// import "../utils/scripts/wdyr";
import "react-perfect-scrollbar/dist/css/styles.css";

import { ToastContainer, Slide } from "react-toastify";
import { IncomingMessage } from "http";
import App, { AppContext } from "next/app";
import Head from "next/head";
import { useRouter } from "next/router";
import React, { useEffect } from "react";
import { useStore } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import { useTradeData } from "@/contexts/../hooks/trade";
import { useDeviceInfo } from "@/utils/hooks/Device";
import { appWithTranslation } from "next-i18next";
import { getLocationParam } from "@/utils/url";
import { wrapper } from "../redux/store";
import { TradeChartGlobalAPIContext, useTradeChartGlobalAPI } from "@/contexts/ChartAPI";
import { useTradeChartLoader } from "@/hooks/chart/loader";
import { ThemeContextProvider } from "@/contexts/color-theme";
import { useChartEngine } from "@/hooks/chart/useChartEngine";

//const defaultUrlQuery = { api_url: undefined };

const MyApp = ({ Component, pageProps }) => {
    const router = useRouter();
    //const { query = defaultUrlQuery, userAgent, meta } = pageProps;
    const store = useStore(state => state);

    useEffect(() => {
        const langFromQuery = getLocationParam("lang");

        if (langFromQuery) {
            router.replace(router.asPath, router.asPath, { locale: langFromQuery });
        }
    }, []);

    const chartEngine = useChartEngine();
    useTradeChartLoader(chartEngine);
    useTradeData();
    useDeviceInfo();

    const chartGlobalAPI = useTradeChartGlobalAPI();

    const preCacheImages = [
        "/sounds/ok.mp3",
        "/images/warning.svg",
        "/images/warning.svg",
        "/images/warning_light.svg",
        "/images/success.svg",
        "/images/success_white.svg",
        "/images/SuccessFeedback.svg",
        "/images/market.svg",
        "/images/market-closed_dark.svg",
        "/images/market-closed_light.svg",
        "/images/market_black.svg",
        "/images/marketClosed.svg",
        "/images/marketClosedBlack.svg",
    ];

    return (
        <ThemeContextProvider>
            <PersistGate persistor={store.__persistor}>
                <ToastContainer
                    position="top-right"
                    autoClose={2000}
                    hideProgressBar
                    limit={3}
                    closeOnClick
                    pauseOnHover
                    transition={Slide}
                    toastStyle={{
                        border: "1px solid #e6ecf0",
                        backgroundColor: "#e6ecf0",
                        color: "#333333",
                        borderRadius: "8px",
                        padding: "10px 16px",
                        minHeight: "auto",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                    }}
                />
                <Head>
                    {preCacheImages &&
                        preCacheImages.map(image => (
                            <link key={`key_${image}`} rel="preload" href={image} as="image" />
                        ))}
                    <link rel="preconnect" href="https://fonts.googleapis.com" />
                    <link rel="preconnect" href="https://fonts.gstatic.com" />
                    <link
                        href="https://fonts.googleapis.com/css2?family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap"
                        rel="stylesheet"
                    />
                    <title>Trading Platform</title>
                </Head>
                <TradeChartGlobalAPIContext.Provider value={chartGlobalAPI}>
                    <Component {...pageProps} />
                </TradeChartGlobalAPIContext.Provider>
            </PersistGate>
        </ThemeContextProvider>
    );
};

const getUserAgent = (req: IncomingMessage): string => {
    return req ? req?.headers["user-agent"] : navigator?.userAgent;
};

const getQueryParams = params => {
    return Object.keys(params).reduce((acc, k) => {
        if (k !== "lang") {
            const query = `${k}=${params[k]}`;
            acc = `${acc}&${query}`;
            if (acc[0] === "&") {
                acc.replace("&", "");
            }
        }

        return acc;
    }, "");
};

MyApp.getInitialProps = async (appContext: AppContext) => {
    const { Component, ctx, router } = appContext;
    const { query, route } = router;
    if (route.includes("error")) {
        if (ctx.res) {
            ctx.res.writeHead(301, {
                Location: `/?${getQueryParams(query)}`,
            });
            ctx.res.end();
        }
    }
    const appProps = await App.getInitialProps(appContext);

    const pageProps = {
        query,
        userAgent: getUserAgent(ctx.req),
        ...(Component.getInitialProps ? await Component.getInitialProps(ctx) : {}),
    };
    return { ...appProps, pageProps };
};

// @ts-ignore
export default wrapper.withRedux(appWithTranslation(MyApp));
